import { createSelector } from 'reselect';
import { compareVersions } from 'compare-versions';

import * as types from './types';

export const getInstallerSlice = (state) => state.hardware.installer;

export const getInstallerExists = createSelector(
  getInstallerSlice,
  (state) => !!state.version,
);

export const getInstalledMSes = createSelector(
  getInstallerSlice,
  (state) => state.installedMS,
);

export const getIsProduction = (state) => getInstallerSlice(state).isProduction;

/**
 * Returns dynamically generated endpoint for MS for given integration
 *
 * Endpoint is generated based on the ms configuration SSLEnable and Port properties
 * @param integration
 */
export const getMSEndpoint = (integrationSlug) =>
  createSelector(getInstalledMSes, (state) => {
    // Find integration by slug
    const integration = state.integrations.find(
      (el) => el.name === types.integrationMapper[integrationSlug].entity,
    );
    return integration?.endpoint || '';
  });

export const getAvailableMSesSlice = createSelector(
  getInstallerSlice,
  (state) => state.availableMS,
);

export const getAvailableMSes = createSelector(getAvailableMSesSlice, (state) =>
  Object.keys(state || {}),
);

/**
 * Gives all related information for given micro-service
 *
 * @param {string} entity - name of micro-service
 * @returns All data related to the MS
 */

export const getMSDataByEntity = (entity) => (state) => {
  //  default micro-service object from the mapper
  let microServiceData = {};
  const msDefaultDetails = types.entityMapper[entity];
  if (!microServiceData) return {};

  microServiceData = {
    ...msDefaultDetails,
    entity,
    msIsInstalled: false,
    msIsAvailable: false,
    builds: [],
    installedVersion: null,
    latestVersion: null,
  };

  //  get builds for this micro-service if its among availableMS
  const availableMicroService = getAvailableMSesSlice(state)[entity];
  if (availableMicroService?.builds?.length) {
    microServiceData.msIsAvailable = true;
    microServiceData.builds =
      availableMicroService.builds.sort(compareVersions);
    microServiceData.latestVersion =
      microServiceData.builds[microServiceData.builds.length - 1];
  }

  //  get latest version of that micro-service if it's installed
  const installedMicroServices = getInstalledMSes(state);
  const installedMS = installedMicroServices.find((ms) => ms.name === entity);

  if (installedMS) {
    microServiceData.msIsInstalled = true;
    microServiceData.installedVersion = installedMS.version;
    const { directory, version, status } = installedMS;
    microServiceData = { ...microServiceData, directory, version, status };
  }

  return microServiceData;
};

/**
 * Gives all related information for given integration
 *
 * @param {string} integrationName - name of integration
 * @returns All data related to the Integration
 */

export const getMSDataByIntegration = (integrationName) => (state) => {
  let integrationData = types.integrationMapper[integrationName];
  if (!integrationData) return {};
  if (integrationData.entity) {
    integrationData = {
      ...integrationData,
      ...getMSDataByEntity(integrationData.entity)(state),
    };
  } else {
    integrationData.msIsAvailable = false;
    integrationData.msIsInstalled = false;
  }

  return integrationData;
};

export const getInstallerIsLoading = createSelector(
  getInstallerSlice,
  (state) => state.loading,
);

export const getInstallerVersion = createSelector(
  getInstallerSlice,
  (state) => state.version,
);
